// Client-side sorting for Chart Browser entries (GitHub issue #112).
//
// Pure, side-effect-free comparator logic over an already-loaded array of
// ChartBrowserEntry objects. No network access, no DOM, no global state — the
// UI owns the current sort state and calls sortChartEntries() on every change.
// This keeps the product's local-first rule intact: re-sorting never triggers a
// new request, it only reorders data the browser already holds.

/**
 * The sort keys exposed in the Chart Browser UI.
 * - rank:   1-based chart position (Apple's published ordering).
 * - name:   app name, case-insensitive locale compare.
 * - artist: developer / seller name, case-insensitive locale compare.
 * - genre:  primary genre name, case-insensitive locale compare.
 * @typedef {'rank'|'name'|'artist'|'genre'} ChartSortKey
 */

/** @typedef {'asc'|'desc'} SortDir */

/** Sort keys backed by a text comparison (everything except numeric rank). */
const TEXT_KEYS = new Set(['name', 'artist', 'genre']);

export const CHART_SORT_KEYS = /** @type {ChartSortKey[]} */ ([
  'rank',
  'name',
  'artist',
  'genre',
]);

/**
 * Extract the raw comparable value for an entry on a given key.
 * @param {import('./chartRank.js').ChartBrowserEntry} entry
 * @param {ChartSortKey} key
 * @returns {number|string|null}
 */
function valueFor(entry, key) {
  switch (key) {
    case 'rank':
      return typeof entry.rank === 'number' && Number.isFinite(entry.rank)
        ? entry.rank
        : null;
    case 'name':
      return entry.name ?? null;
    case 'artist':
      return entry.artistName ?? null;
    case 'genre':
      return entry.primaryGenre ?? null;
    default:
      return null;
  }
}

/**
 * Return a re-sorted copy of the chart entries. The input array is never
 * mutated. Entries whose value is null/empty for the active key always sink to
 * the bottom regardless of direction, so a missing developer or genre never
 * displaces real data at the top of the list. Ties fall back to chart rank so
 * the order stays stable and predictable.
 *
 * @param {import('./chartRank.js').ChartBrowserEntry[]} entries
 * @param {ChartSortKey} key
 * @param {SortDir} [dir='asc']
 * @returns {import('./chartRank.js').ChartBrowserEntry[]}
 */
export function sortChartEntries(entries, key, dir = 'asc') {
  if (!Array.isArray(entries)) return [];
  if (!CHART_SORT_KEYS.includes(key)) return [...entries];

  const factor = dir === 'desc' ? -1 : 1;
  const isText = TEXT_KEYS.has(key);

  return [...entries].sort((a, b) => {
    const av = valueFor(a, key);
    const bv = valueFor(b, key);

    // Nulls / empty strings always sort last, both directions.
    const aMissing = av === null || av === '';
    const bMissing = bv === null || bv === '';
    if (aMissing && bMissing) return tieBreak(a, b);
    if (aMissing) return 1;
    if (bMissing) return -1;

    let cmp;
    if (isText) {
      cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
    } else {
      cmp = /** @type {number} */ (av) - /** @type {number} */ (bv);
    }

    if (cmp !== 0) return cmp * factor;
    return tieBreak(a, b);
  });
}

/**
 * Stable secondary ordering by chart rank (lowest rank first). Keeps equal
 * primary values in a deterministic order across browsers.
 */
function tieBreak(a, b) {
  const ar = typeof a.rank === 'number' ? a.rank : Number.POSITIVE_INFINITY;
  const br = typeof b.rank === 'number' ? b.rank : Number.POSITIVE_INFINITY;
  return ar - br;
}
